import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import {
  USER_ROLE_ADMIN,
  UserRoleType,
  USER_ROLE_AUTHOR,
  USER_ROLE_MANAGER,
  USER_ROLE_VIEWER,
  USER_ROLE_AUTHOR_REDACT,
  USER_ROLE_VIEWER_REDACT,
} from 'cio/utils/user-roles';
import AuthService from 'cio/services/auth';
import MasqService from 'cio/services/masq';
import MomentService from 'cio/services/moment';
import EnvironmentService from 'cio/services/environment';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountModel from 'cio/models/account';
import AccountService from 'cio/services/account';
import { ProductApp } from 'cio/utils/product-app-info';

interface SettingsPageLink {
  icon: string;
  title: string;
  description: string;
  url: string;
  routeQuery?: Record<string, string | null>;
  restrictedToRoles: UserRoleType[];
  restrictedToNewBillingEnabled?: boolean;
  restrictedToProducts?: ProductApp[];
}

export const SETTING_LINKS_ALL: Record<string, SettingsPageLink> = {
  account: {
    icon: 'account-box',
    title: 'Account',
    description: 'Edit your basic account and company information, as well as add contact email addresses.',
    url: 'settings.account',
    restrictedToRoles: [USER_ROLE_ADMIN],
  },
  workspaces: {
    icon: 'workspaces',
    title: 'Workspaces',
    description: 'Workspaces help you work with multiple products, apps, or user databases.',
    url: 'settings.workspaces',
    restrictedToRoles: [USER_ROLE_ADMIN, USER_ROLE_MANAGER, USER_ROLE_AUTHOR, USER_ROLE_AUTHOR_REDACT],
  },
  security: {
    icon: 'security',
    title: 'Security',
    description: 'Enable security settings like single sign-on and 2FA.',
    url: 'settings.security',
    restrictedToRoles: [USER_ROLE_ADMIN],
  },
  apiCredentials: {
    icon: 'api-credentials',
    title: 'API Credentials',
    description: 'These credentials allow you to send information about your customers to Customer.io.',
    url: 'settings.api_credentials',
    routeQuery: { workspace: null },
    restrictedToRoles: [USER_ROLE_ADMIN, USER_ROLE_MANAGER, USER_ROLE_AUTHOR, USER_ROLE_AUTHOR_REDACT],
    restrictedToProducts: [ProductApp.JOURNEYS],
  },
  team: {
    icon: 'person',
    title: 'Team Members',
    description: 'View and manage members of your team.',
    url: 'settings.team',
    restrictedToRoles: [
      USER_ROLE_ADMIN,
      USER_ROLE_MANAGER,
      USER_ROLE_AUTHOR,
      USER_ROLE_AUTHOR_REDACT,
      USER_ROLE_VIEWER,
      USER_ROLE_VIEWER_REDACT,
    ],
  },
  plansBilling: {
    icon: 'payment',
    title: 'Plans & Billing',
    description: 'New plans, payment info and billing history view.',
    url: 'settings.billing.your-plan',
    restrictedToRoles: [USER_ROLE_ADMIN],
    restrictedToNewBillingEnabled: true,
  },
  privacyAndData: {
    icon: 'data-privacy',
    title: 'Privacy & Data',
    description: 'View data privacy and security resources.',
    url: 'settings.privacy',
    restrictedToRoles: [
      USER_ROLE_ADMIN,
      USER_ROLE_MANAGER,
      USER_ROLE_AUTHOR,
      USER_ROLE_AUTHOR_REDACT,
      USER_ROLE_VIEWER,
      USER_ROLE_VIEWER_REDACT,
    ],
  },
  experimentalFeatures: {
    icon: 'experiment',
    title: 'Experimental Features',
    description: `Test and provide feedback on new features we're working on.`,
    url: 'settings.experimental-features',
    restrictedToRoles: [
      USER_ROLE_ADMIN,
      USER_ROLE_MANAGER,
      USER_ROLE_AUTHOR,
      USER_ROLE_AUTHOR_REDACT,
      USER_ROLE_VIEWER,
      USER_ROLE_VIEWER_REDACT,
    ],
    restrictedToProducts: [ProductApp.JOURNEYS],
  },
};

export default class SettingsIndexController extends Controller {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service masq!: MasqService;
  @service moment!: MomentService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;

  declare model: AccountModel;

  get showAccountSummary() {
    return this.auth.hasPermission('account/view') && !this.account.isPipelinesOnlyPlan;
  }

  get settingLinksDisplayed() {
    return Object.keys(SETTING_LINKS_ALL)
      .map((key) => {
        return {
          ...SETTING_LINKS_ALL[key],
          disabled: this.#isDisabled(SETTING_LINKS_ALL[key]),
        };
      })
      .filter((route) => {
        let canView = route.restrictedToRoles.includes(this.auth.user?.role ?? ('' as any));

        if (route.restrictedToProducts != null) {
          canView =
            canView &&
            this.account.accessibleProducts.filter((p) => route.restrictedToProducts!.includes(p)).length > 0;
        }
        return canView;
      });
  }

  #isDisabled(link: SettingsPageLink) {
    return !this.auth.hasPermission('account/api_credentials/view') && link.url === 'settings.api_credentials';
  }
}
